export const CANVAS_WIDTH = 360;

export type StudioFormatId = 'post' | 'story';

export interface StudioFormat {
  id: StudioFormatId;
  labelId: string;
  ratio: string;
  width: number;
  height: number;
}

export const STUDIO_FORMATS: Record<StudioFormatId, StudioFormat> = {
  post: {
    id: 'post',
    labelId: 'hot-topic-studio-format-post',
    ratio: '4:5',
    width: 1080,
    height: 1350
  },
  story: {
    id: 'story',
    labelId: 'hot-topic-studio-format-story',
    ratio: '9:16',
    width: 1080,
    height: 1920
  }
};

export const STUDIO_LANGUAGES = [
  { id: 'zh-Hant-HK', label: '繁體' },
  { id: 'zh', label: '简体' },
  { id: 'en', label: 'EN' }
] as const;

export type StudioTemplateId = 'classic' | 'editorial' | 'spotlight';

export const STUDIO_TEMPLATES: Array<{ id: StudioTemplateId; labelId: string }> = [
  { id: 'classic', labelId: 'hot-topic-studio-template-classic' },
  { id: 'editorial', labelId: 'hot-topic-studio-template-editorial' },
  { id: 'spotlight', labelId: 'hot-topic-studio-template-spotlight' }
];

export const STUDIO_THEME_COLORS = [
  '#1456f0', // signature blue
  '#ff5530', // coral
  '#ea5ec1', // magenta
  '#7c3aed', // violet
  '#0ea5e9', // sky
  '#059669', // emerald
  '#0f766e', // teal
  '#e11d48', // rose
  '#f59e0b', // amber
  '#b45309', // caramel
  '#475569', // slate
  '#1e3a8a', // navy
  '#be185d' // berry
];

export const DEFAULT_STUDIO_THEME_COLOR = STUDIO_THEME_COLORS[0];

export type StudioWatermarkLogoId =
  | 'coin'
  | 'user'
  | 'compass'
  | 'trophy'
  | 'star'
  | 'leaf'
  | 'gift'
  | 'chart'
  | 'idea'
  | 'shield';

export interface StudioSettings {
  locale: string;
  format: string;
  template: string;
  themeColor: string;
  profileName: string;
  watermarkLogo?: StudioWatermarkLogoId | null;
  pageCount?: number;
  showChubbDisclaimer?: boolean;
  // Profile+ adviser URL for the last-page QR. Computed in the editor and
  // sent to the Lambda — the isolated PDF template has no Redux / session.
  qrCodeUrl?: string;
}

export const STUDIO_PREFERENCE_SLOT_COUNT = 3;

export interface StudioPreferences {
  name?: string;
  locale: string;
  format: string;
  template: string;
  themeColor: string;
  profileName: string;
  watermarkLogo?: StudioWatermarkLogoId | null;
}

export type StudioPreferenceSlot = StudioPreferences | null;

export interface StudioPreferencesStore {
  activeSlot: number;
  slots: StudioPreferenceSlot[];
}

export interface StudioPageContent {
  title?: string;
  subtitle?: string;
  bullets?: string[];
  quote?: string;
  author?: string;
  stat?: string;
  label?: string;
  description?: string;
  eyebrow?: string;
  [key: string]: any;
}

export interface StudioPageData {
  layoutType: string;
  content?: Record<string, StudioPageContent>;
}

export const getCanvasHeight = (format: string): number => {
  const selectedFormat = STUDIO_FORMATS[format as StudioFormatId] || STUDIO_FORMATS.post;
  return Math.round((CANVAS_WIDTH * selectedFormat.height) / selectedFormat.width);
};
